import React from "react"
import { useTranslation } from "react-i18next"

import { useFormatDecimal } from "@/formatters/use-format-decimal"
import { DEFAULT_DECIMAL_PLACEHOLDER } from "@/utils/decimal"

import Typography from "@mui/material/Typography"

interface DataGridRecordCountProps {
  count?: number
  isLoading?: boolean
}

const wholeNumberFormat = {
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
} as const

/**
 * Remembers the last count that arrived while nothing was in flight.
 * The first load stays unknown. A later load keeps the remembered count
 * until the next count arrives, including a filter change.
 */
const useSettledRecordCount = ({
  count,
  isLoading,
}: {
  count: number | undefined
  isLoading: boolean
}): number | undefined => {
  const [settledCount, setSettledCount] = React.useState<number | undefined>(
    undefined
  )

  if (!isLoading && count !== undefined && count !== settledCount) {
    setSettledCount(count)
  }

  if (isLoading || count === undefined) {
    return settledCount
  }

  return count
}

/**
 * Record count for a grid toolbar. Remembers the last settled count so a
 * filter change can keep showing it. Does not read Data Grid hooks, so a
 * caller can render it when no grid is mounted.
 */
export const DataGridRecordCount: React.FC<DataGridRecordCountProps> = ({
  count,
  isLoading = false,
}) => {
  const { t } = useTranslation("common")
  const { formatDecimal } = useFormatDecimal(wholeNumberFormat)
  const settledCount = useSettledRecordCount({ count, isLoading })
  const formattedCount =
    typeof settledCount === "number"
      ? formatDecimal(settledCount)
      : DEFAULT_DECIMAL_PLACEHOLDER
  // An unknown total stays on the plural form ("-- records").
  const recordLabelCount = settledCount ?? 0

  return (
    <Typography component="span" sx={{ whiteSpace: "nowrap" }} variant="body2">
      {`${formattedCount} ${t("dataGrid.records", { count: recordLabelCount })}`}
    </Typography>
  )
}
